import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class AirtableService {
  private readonly LAMBDA_ENDPOINT = `${environment.airtableLambdaUrl}/airtable`;

  constructor(private http: HttpClient) {
    // Make service available globally for testing
    if (typeof window !== 'undefined') {
      (window as any).airtableService = this;
    }
  }

  // Fetch all records from any table (no filtering)
  public async fetchAllRecords(tableName: string) {
    // console.log(`Fetching all records from table: ${tableName}`);
    
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetchAll',
        tableName
      })
    );
    
    const records = response.data;
    // console.log(`Retrieved ${records.length} records from ${tableName}`);
    // console.log('Records:', records.map((record: any) => ({
    //   id: record.id,
    //   fields: record.fields
    // })));
    
    return records;
  }

  // Fetch records with custom options
  public async fetchRecords(tableName: string, options: any = {}) {
    // console.log(`Fetching records from table: ${tableName}`, options);
    
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName,
        options
      })
    );
    
    const records = response.data;
    // console.log(`Retrieved ${records.length} records`);
    // console.log('Records:', records.map((record: any) => ({
    //   id: record.id,
    //   fields: record.fields
    // })));
    
    return records;
  }

  // Fetch active surveys within date range
  public async fetchActiveSurveys() {
    // console.log('Fetching active surveys');
    
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    // console.log('Today\'s date for filtering:', today);
    
    // Get all active surveys and filter dates in JavaScript (simplified approach)
    // console.log('Using JavaScript filtering approach...');
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName: 'Surveys',
        options: {
          filterByFormula: `{Active} = 1`
        }
      })
    );
    const records = response.data;
    
    // console.log(`Found ${records.length} active surveys, now filtering by date...`);
    
    // Filter dates in JavaScript
    const filteredRecords = records.filter((record: any) => {
      const startDate = record.fields['Start Date'];
      const endDate = record.fields['End Date'];
      
      // Skip if dates are missing
      if (!startDate || !endDate) {
        // console.log(`Survey "${record.fields['Name']}" - Missing dates (Start: ${startDate}, End: ${endDate})`);
        return false;
      }
      
      const isInRange = startDate <= today && endDate >= today;
      // console.log(`Survey "${record.fields['Name']}" - Start: ${startDate}, End: ${endDate}, Today: ${today}, InRange: ${isInRange}`);
      return isInRange;
    });
    
    // console.log(`Retrieved ${filteredRecords.length} active surveys after date filtering`);
    if (filteredRecords.length > 0) {
      // console.log('Filtered active surveys:', filteredRecords.map((r: any) => ({
      //   id: r.id,
      //   name: r.fields['Name'],
      //   startDate: r.fields['Start Date'],
      //   endDate: r.fields['End Date'],
      //   active: r.fields['Active']
      // })));
      
      // Log complete survey structure for debugging
      // console.log('DEBUG: Complete survey structure:');
      // filteredRecords.forEach((survey: any) => {
      //   console.log(`Survey ${survey.id}:`, {
      //     id: survey.id,
      //     fields: survey.fields,
      //     allFieldNames: Object.keys(survey.fields || {})
      //   });
      // });
    }
    
    return filteredRecords;
  }

  // Fetch questions by their IDs
  public async fetchQuestionsByIds(questionIds: string[]) {
    if (!questionIds || questionIds.length === 0) {
      // console.log('No question IDs provided');
      return [];
    }
    
    // console.log(`Fetching questions by IDs: ${questionIds.join(', ')}`);
    
    // Build OR formula for multiple IDs
    const orConditions = questionIds.map(id => `RECORD_ID() = '${id}'`).join(', ');
    const filterFormula = questionIds.length === 1 ? orConditions : `OR(${orConditions})`;
    
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName: 'Questions',
        options: {
          filterByFormula: filterFormula
        }
      })
    );
    
    const records = response.data;
    // console.log(`Retrieved ${records.length} questions`);
    // console.log('Question details:', records.map((record: any) => ({
    //   id: record.id,
    //   fields: record.fields,
    //   fieldNames: Object.keys(record.fields || {})
    // })));
    
    return records;
  }

  // Fetch responses by their IDs
  public async fetchResponsesByIds(responseIds: string[]) {
    if (!responseIds || responseIds.length === 0) {
      // console.log('No response IDs provided');
      return [];
    }
    
    // console.log(`Fetching responses by IDs: ${responseIds.join(', ')}`);
    
    // Build OR formula for multiple IDs
    const orConditions = responseIds.map(id => `RECORD_ID() = '${id}'`).join(', ');
    const filterFormula = responseIds.length === 1 ? orConditions : `OR(${orConditions})`;
    
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName: 'Responses',
        options: {
          filterByFormula: filterFormula
        }
      })
    );
    
    const records = response.data;
    // console.log(`Retrieved ${records.length} responses`);
    // console.log('Response details:', records.map((record: any) => ({
    //   id: record.id,
    //   userId: record.fields?.['User ID'],
    //   surveyId: record.fields?.['Survey ID'],
    //   answer: record.fields?.['Answer']
    // })));
    
    return records;
  }

  // Fetch questions for a specific survey (DEPRECATED - use fetchQuestionsByIds with survey.fields.Questions)
  public async fetchSurveyQuestions(surveyId: string) {
    // console.log(`DEPRECATED: fetchSurveyQuestions called for survey: ${surveyId}`);
    // console.log('This method is deprecated. Surveys now contain a Questions field with question IDs.');
    // console.log('Use fetchQuestionsByIds with the Questions array from the survey instead.');
    
    // For backward compatibility, still try to fetch questions
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName: 'Questions',
        options: {
          filterByFormula: `SEARCH('${surveyId}', ARRAYJOIN({Survey ID}, ','))`
        }
      })
    );
    
    return response.data;
  }

  // Create a survey response
  public async createSurveyResponse(surveyId: string, questionId: string, userId: string, answer: string, metadata: any) {
    // console.log('Creating survey response', { surveyId, questionId, userId, answer });
    
    // Log the full metadata object
    // console.log('Full metadata object:', JSON.stringify(metadata, null, 2));
    
    // Log individual user fields
    // console.log('User metadata fields:', {
    //   name: metadata.user?.name,
    //   buildingId: metadata.user?.buildingId,
    //   unitName: metadata.user?.unitName,
    //   floor: metadata.user?.floor
    // });
    
    // Validate IDs are strings and not empty
    if (!surveyId || typeof surveyId !== 'string') {
      throw new Error(`Invalid survey ID: ${surveyId}`);
    }
    if (!questionId || typeof questionId !== 'string') {
      throw new Error(`Invalid question ID: ${questionId}`);
    }
    
    // Prepare the fields object - matching the exact structure from the sample
    const fields: any = {
      'Survey ID': [surveyId],
      'Question ID': [questionId],
      'User ID': userId,
      'Answer': answer,
      'Name': metadata.user?.name || '',
      // Building field removed - requires Airtable record ID, not UUID
      // Consider adding 'Building UUID' text field in Airtable and using automation
      'Apartment Name': metadata.user?.unitName || '',
      'Floor': metadata.user?.floor || '',
      'Metadata': JSON.stringify(metadata),
      'App OS': metadata.device?.platform || metadata.device?.operatingSystem || '',
      'App Version': metadata.app?.version || ''
    };
    
    // Add Building UUID as a text field if you want to track it
    if (metadata.user?.buildingId) {
      fields['Building UUID'] = metadata.user.buildingId;
    }
    
    // console.log('Fields being sent to Airtable:', fields);
    
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'create',
        tableName: 'Responses',
        data: [
          {
            fields: fields
          }
        ]
      })
    );
    
    const record = response.data;
    // console.log('Survey response created:', record[0].id);
    return record[0];
  }

  // Update survey response with notes
  public async updateSurveyResponseNotes(responseId: string, notes: string) {
    // console.log('Updating survey response notes', { responseId, notes });
    
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'update',
        tableName: 'Responses',
        data: [
          {
            id: responseId,
            fields: {
              'Notes': notes
            }
          }
        ]
      })
    );
    
    const record = response.data;
    // console.log('Survey response updated');
    return record[0];
  }

  // Check if user has already responded to a survey
  public async hasUserRespondedToSurvey(surveyId: string, userId: string): Promise<boolean> {
    // console.log('Checking if user has responded to survey', { surveyId, userId });

    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName: 'Responses',
        options: {
          filterByFormula: `AND(
            {Survey ID} = '${surveyId}',
            {User ID} = '${userId}'
          )`,
          maxRecords: 1
        }
      })
    );

    const records = response.data;
    const hasResponded = records.length > 0;
    // console.log(`User has ${hasResponded ? 'already' : 'not'} responded to survey`);
    return hasResponded;
  }

  // Fetch all responses for a user on a specific survey (for multi-question resume support)
  public async fetchUserResponsesForSurvey(surveyId: string, userId: string): Promise<any[]> {
    // console.log('Fetching user responses for survey', { surveyId, userId });

    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName: 'Responses',
        options: {
          filterByFormula: `AND(FIND('${surveyId}', ARRAYJOIN({Survey ID}, ',')), {User ID} = '${userId}')`
        }
      })
    );

    const records = response.data;
    // console.log(`Found ${records.length} responses for user on survey`);
    return records;
  }

  // Fetch building records by their Airtable record IDs
  public async fetchBuildingsByRecordIds(recordIds: string[]) {
    if (!recordIds || recordIds.length === 0) {
      console.log('No building record IDs provided');
      return [];
    }
    
    console.log(`Fetching buildings by record IDs: ${recordIds.join(', ')}`);
    
    // Build OR formula for multiple IDs
    const orConditions = recordIds.map(id => `RECORD_ID() = '${id}'`).join(', ');
    const filterFormula = recordIds.length === 1 ? orConditions : `OR(${orConditions})`;
    
    const response = await firstValueFrom(
      this.http.post<any>(this.LAMBDA_ENDPOINT, {
        action: 'fetch',
        tableName: 'Buildings',
        options: {
          filterByFormula: filterFormula
        }
      })
    );
    
    const records = response.data;
    console.log(`Retrieved ${records.length} building records`);
    console.log('Building details:', records.map((record: any) => ({
      id: record.id,
      buildingUUID: record.fields?.['Building ID'],
      name: record.fields?.['Name']
    })));
    
    return records;
  }

  // Dismissals Methods

  // Check if a dismissal record exists for user/survey combination
  public async getDismissalRecord(surveyId: string, userId: string): Promise<any | null> {
    try {
      const filterFormula = `AND({Survey ID} = '${surveyId}', {User ID} = '${userId}')`;
      
      const response = await firstValueFrom(
        this.http.post<any>(this.LAMBDA_ENDPOINT, {
          action: 'fetch',
          tableName: 'Dismissals',
          options: {
            filterByFormula: filterFormula,
            maxRecords: 1
          }
        })
      );
      
      const records = response.data;
      return records.length > 0 ? records[0] : null;
    } catch (error) {
      console.error('Error fetching dismissal record:', error);
      return null;
    }
  }

  // Create or update dismissal record
  public async createOrUpdateDismissal(surveyId: string, questionId: string, userId: string, metadata: any): Promise<any> {
    try {
      // First check if a dismissal record already exists
      const existingRecord = await this.getDismissalRecord(surveyId, userId);
      
      if (existingRecord) {
        // Update existing record - increment dismissal count
        const currentCount = existingRecord.fields['Dismissal Count'] || 0;
        
        const response = await firstValueFrom(
          this.http.post<any>(this.LAMBDA_ENDPOINT, {
            action: 'update',
            tableName: 'Dismissals',
            data: [
              {
                id: existingRecord.id,
                fields: {
                  'Dismissal Count': currentCount + 1,
                  'Metadata': JSON.stringify(metadata),
                  'App OS': metadata.device?.platform || metadata.device?.operatingSystem || '',
                  'App Version': metadata.app?.version || '',
                  'HA Version': metadata.home?.version || '',
                  'VOS Version': metadata.vos?.version || ''
                }
              }
            ]
          })
        );
        
        console.log('Dismissal record updated, new count:', currentCount + 1);
        return response.data[0];
      } else {
        // Create new dismissal record
        const fields: any = {
          'Survey ID': [surveyId],
          'Question ID': [questionId],
          'User ID': userId,
          'Dismissal Count': 1,
          'Name': metadata.user?.name || '',
          'Apartment Name': metadata.user?.unitName || '',
          'Floor': metadata.user?.floor || '',
          'Metadata': JSON.stringify(metadata),
          'App OS': metadata.device?.platform || metadata.device?.operatingSystem || '',
          'App Version': metadata.app?.version || ''
        };
        
        // Add Building UUID as a text field
        if (metadata.user?.buildingId) {
          fields['Building UUID'] = metadata.user.buildingId;
        }
        
        // Add version fields if available
        if (metadata.home?.version) {
          fields['HA Version'] = metadata.home.version;
        }
        if (metadata.vos?.version) {
          fields['VOS Version'] = metadata.vos.version;
        }
        
        const response = await firstValueFrom(
          this.http.post<any>(this.LAMBDA_ENDPOINT, {
            action: 'create',
            tableName: 'Dismissals',
            data: [
              {
                fields: fields
              }
            ]
          })
        );
        
        console.log('New dismissal record created');
        return response.data[0];
      }
    } catch (error) {
      console.error('Error creating/updating dismissal:', error);
      throw error;
    }
  }
}